import { Box, Stack } from '@mui/material';
import QueryBuilder from 'components/dataset-viewer/query-builder';
import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import DataPreview from 'app/editor/dataset/DataPreview';
import { reportAlertRuleAPI, ReportAlertRuleDataResponse } from 'api/viewer/report-alert-rule';
import useBundleTranslation from 'i18n';
import { PopupSettings } from 'components/common/popup/Popup';
import { stringifyFilter } from 'components/dataset-viewer/query-builder/helpers';
import { getInitialFilterState, InstanceMode, RulesConfig } from 'components/dataset-viewer';
import InstanceInfo from 'components/dataset-viewer/instance-info';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { setBreadcrumbs } from 'store/breadcrumbsSlice';
import { useDispatch } from 'react-redux';
import { SortType } from 'rsuite-table/lib/types';
import { cloneDeep, isEqual } from 'lodash';
import { setPageTitle } from 'store/pageTitleSlice';
import useOnLeavePrompt from 'hooks/useOnLeavePrompt';
import { useMiNavigation } from 'hooks/useNavigation';
import Infobar from './Infobar';
import { FormComponentValue } from 'components/common/form/layout/control';
import { buildDisplayTemplateSuggestion } from './index';
import DisplayTemplateField from './DisplayTemplateField';
import ErrorPopup from './ErrorPopup';
import { BlocksContext } from '@report-content';
import TrackChangesTab from 'components/dataset-viewer/query-builder/fields-selector/TrackChangesTab';
import SimpleFieldsSelectorTable from 'components/dataset-viewer/query-builder/fields-selector/SimpleFieldsSelectorTable';
import ReportAlertInstanceModeSelection from '../report-alert-rule-wizard/ReportAlertInstanceModeSelection';
import { IRuleDisplayTemplate } from './useRuleDisplayTemplate';

export default function ReportAlertRule({
    elementId,
    reportAlertRuleId,
    displayMode,
    wizardStep,
    ruleData,
    loadRuleData,
    nameComponent,
    displayTemplate,
    onJsonChange,
}: {
    elementId: number;
    reportAlertRuleId: number;
    displayMode: 'page' | 'wizard';
    wizardStep: number;
    ruleData?: ReportAlertRuleDataResponse;
    loadRuleData: () => Promise<any>;
    nameComponent?: ReactNode;
    displayTemplate: IRuleDisplayTemplate;
    onJsonChange?: (json: string) => void;
}) {
    const { t } = useBundleTranslation(['app/report/report-alert-rule', 'components/dataset-viewer/dataset_viewer']);
    const reportFilters =
        useContext(BlocksContext)?.manageFiltersSettings?.fields.filter((f) => f.filter_column == 'Y') ?? [];
    const initFilterState = useMemo(
        () => ({
            ...getInitialFilterState(),
            trackRemovedRows: displayMode == 'page',
            trackNewRows: displayMode == 'page',
        }),
        [],
    );

    const navigate = useMiNavigation();
    const dispatch = useDispatch();

    const [rulesSelectData, setRulesSelectData] = useState<FormComponentValue[]>([]);
    const [filterDirty, setFilterDirty] = useState(false);
    const [builderDirty, setBuilderDirty] = useState(false);
    const [json, setJson] = useState(stringifyFilter(initFilterState));
    const [json4Preview, setJson4Preview] = useState(stringifyFilter(initFilterState));
    const [showLoader, setShowLoader] = useState(false);
    const [filter, setFilter] = useState<RulesConfig>(cloneDeep(initFilterState));
    const [dataPreviewUrl, setDataPreviewUrl] = useState('');
    const [order, setOrder] = useState<SortType>('asc');
    const [orderBy, setOrderBy] = useState('');
    const [errorPopupMessage, setErrorPopupMessage] = useState('');
    const [dataPreviewResized, setDataPreviewResized] = useState(0);
    const [errorPopupTitle, setErrorPopupTitle] = useState('error');
    const [currentInstance, setCurrentInstance] = useState('');
    const [priorInstance, setPriorInstance] = useState('');
    const [instanceMode, setInstanceMode] = useState<InstanceMode>('current');
    const [isDefaultTemplate, setIsDefaultTemplate] = useState(false);
    const [showInstancesSelector, setShowInstancesSelector] = useState(false);
    const autoAddedFieldIds = useRef<Set<string>>(new Set());

    useEffect(() => {
        onJsonChange?.(json);
    }, [json]);

    useOnLeavePrompt(t('form_leave_confirm'), filterDirty && displayMode == 'page', 'page');

    useEffect(() => {
        setBuilderDirty(false);
        setFilterDirty(false);
    }, [ruleData]);

    // Used in Wizard Mode
    const usedReportFields = useMemo(() => {
        if (!ruleData?.columnsData) {
            return [];
        }
        return ruleData.columnsData.filter((column) =>
            reportFilters.some((f) => f.reference_name == column.ref_column_name),
        );
    }, [ruleData?.columnsData]);

    useEffect(() => {
        if (ruleData) {
            const { alertRuleData, reportData, columnsData, datasetData } = ruleData;
            const isInstancesMode: boolean = datasetData.save_historical_instances_ind === 'Y';
            setShowInstancesSelector(isInstancesMode);

            const parsedRulesData = alertRuleData.rules_serialized ? JSON.parse(alertRuleData.rules_serialized) : null;

            if (parsedRulesData) {
                if (!parsedRulesData.fieldsOrder) {
                    parsedRulesData.fieldsOrder = [];
                    parsedRulesData.resultFields = [];
                }
                setFilter(parsedRulesData);
                setJson(alertRuleData.rules_serialized);
                setJson4Preview(alertRuleData.rules_serialized);
                setInstanceMode(parsedRulesData.history ? 'lastTwo' : 'current');
            } else {
                const newFilter = cloneDeep(initFilterState);
                if (displayMode == 'wizard') {
                    newFilter.resultFields = usedReportFields.map((column) => column.id);
                    newFilter.joinFields = usedReportFields.map((column) => ({
                        id: column.id,
                        name: column.name,
                        column_name: column.column_name,
                        ref_column_name: column.ref_column_name,
                    }));
                } else {
                    newFilter.resultFields = columnsData.map((column) => column.id);
                }

                newFilter.resultFieldAggregations = {};
                setFilter(newFilter);
                setJson(stringifyFilter(newFilter));
                setJson4Preview(stringifyFilter(newFilter));
            }

            const parsedFilterForSuggestion = parsedRulesData ?? cloneDeep(initFilterState);
            const computedSuggestion = buildDisplayTemplateSuggestion(
                alertRuleData.name,
                columnsData,
                parsedFilterForSuggestion,
            );
            if (alertRuleData.rule_display_template) {
                displayTemplate.setRuleDisplayTemplate(alertRuleData.rule_display_template);
                setIsDefaultTemplate(alertRuleData.rule_display_template === computedSuggestion);
            } else {
                displayTemplate.setRuleDisplayTemplate(computedSuggestion);
                setIsDefaultTemplate(true);
            }

            setDataPreviewUrl(`/data/report-alert-rule/${elementId}/alert-rule/${reportAlertRuleId}/run-sql`);
            if (displayMode == 'page') {
                dispatch(
                    setBreadcrumbs([
                        {
                            label: t('breadcrumbs.grid'),
                            href: `/editor/report`,
                        },
                        {
                            label: reportData.name,
                            href: `/editor/element/report/${elementId}`,
                            translate: false,
                        },
                        {
                            label: t('breadcrumbs.alert_rules'),
                        },
                        {
                            label: alertRuleData.name,
                            translate: false,
                        },
                    ]),
                );
                dispatch(setPageTitle(`${t('breadcrumbs.page_title')} ${alertRuleData.name}`));
            }

            setRulesSelectData(
                ruleData.allAlertRules.map((rule) => ({
                    label: rule.name,
                    value: String(rule.report_alert_rule_id),
                })),
            );
        }
    }, [ruleData, usedReportFields, displayMode, ruleData?.alertRuleData.name]);

    const onCurrentFilterUpdate = useCallback(
        (newFilter: RulesConfig) => {
            const currentFilterNoOrder = { ...filter, fieldsOrder: [] };
            const newFilterNoOrder = { ...newFilter, fieldsOrder: [] };

            if (isEqual(currentFilterNoOrder, newFilterNoOrder)) {
                return;
            }

            const isOnlyFieldsOrderChange =
                filter.fieldsOrder &&
                filter.fieldsOrder.length === 0 &&
                newFilter.fieldsOrder &&
                newFilter.fieldsOrder.length > 0 &&
                filter.resultFields.length === newFilter.resultFields.length &&
                isEqual(filter.resultFieldAggregations, newFilter.resultFieldAggregations) &&
                isEqual(filter.derivedFields, newFilter.derivedFields) &&
                isEqual(filter.group, newFilter.group);

            if (isOnlyFieldsOrderChange) {
                setJson(stringifyFilter(newFilter));
                setFilter(newFilter);
                return;
            }

            setFilterDirty(true);
            setBuilderDirty(true);
            if (displayMode == 'wizard') {
                const collectFieldIds = (group: typeof newFilter.group): Set<string> => {
                    const fields = new Set<string>();
                    for (const rule of group.rules) {
                        if (rule.group) {
                            collectFieldIds(rule.group).forEach((f) => fields.add(f));
                        } else if (rule.field) {
                            fields.add(rule.field);
                        }
                    }
                    return fields;
                };
                const usedFilterFieldIds = collectFieldIds(newFilter.group);

                const usedReportFieldIds = new Set(usedReportFields.map((c) => c.id));
                const numericFieldsInRules = (ruleData?.columnsData ?? []).filter(
                    (column) =>
                        usedFilterFieldIds.has(column.id) &&
                        column.value_type == 'numeric' &&
                        !usedReportFieldIds.has(column.id),
                );

                // Fields to add: numeric, in current rules, not yet in resultFields
                const toAdd = numericFieldsInRules.filter(
                    (column) => !(newFilter.resultFields ?? []).includes(column.id),
                );

                // Fields to remove: previously auto-added by us, no longer present in rules
                const toRemove = new Set([...autoAddedFieldIds.current].filter((id) => !usedFilterFieldIds.has(id)));

                // Update auto-tracked set
                toRemove.forEach((id) => autoAddedFieldIds.current.delete(id));
                toAdd.forEach((column) => autoAddedFieldIds.current.add(column.id));

                // Apply to newFilter
                newFilter.resultFields = [
                    ...(newFilter.resultFields ?? []).filter((id) => !toRemove.has(id)),
                    ...toAdd.map((c) => c.id),
                ];
                const newAggregations = { ...newFilter.resultFieldAggregations };
                toRemove.forEach((id) => delete newAggregations[id]);
                toAdd.forEach((column) => {
                    newAggregations[column.id] = 'Sum';
                });
                newFilter.resultFieldAggregations = newAggregations;
            }
            setJson(stringifyFilter(newFilter));
            setFilter(newFilter);
        },
        [filter],
    );

    const areEqualIgnoringOrder = useCallback((a: string[], b: string[]) => {
        if (a.length !== b.length) return false;
        const aSet = new Set(a);
        return b.every((val) => aSet.has(val));
    }, []);

    const handleFieldsChange = useCallback(
        (newSelectedColumns: string[]) => {
            if (!ruleData) {
                return;
            }

            const currentFields = filter.resultFields || [];

            if (areEqualIgnoringOrder(currentFields, newSelectedColumns)) {
                return;
            }

            const newFilter = { ...filter };
            newFilter.resultFields = newSelectedColumns;

            onCurrentFilterUpdate(newFilter);
        },
        [filter, ruleData, onCurrentFilterUpdate, areEqualIgnoringOrder],
    );

    const applyChanges = () => {
        if (builderDirty) {
            setJson4Preview(json);
            setBuilderDirty(false);
        }
    };

    const saveRule = async () => {
        if (!displayTemplate.ruleDisplayTemplate.trim()) {
            displayTemplate.setRuleDisplayTemplateError(true);
            return false;
        }

        setShowLoader(true);

        return await reportAlertRuleAPI
            .saveAlertRule(elementId, reportAlertRuleId, json, displayTemplate.ruleDisplayTemplate)
            .then((response) => {
                if (response.status === 'OK') {
                    setBuilderDirty(false);
                    setFilterDirty(false);
                    setErrorPopupTitle('error');

                    return true;
                }

                if (response.status === 'ERROR') {
                    if (response.message) {
                        setErrorPopupMessage(response.message);
                    } else {
                        setErrorPopupMessage(t('visibility.error_switch_visibility'));
                    }
                    setErrorPopupTitle('view.error_title');
                    return false;
                }
                return false;
            })
            .finally(() => {
                setShowLoader(false);
            });
    };

    const onSortColumn = useCallback(
        (dataKey: string, sortType?: SortType) => {
            const newFilter = { ...filter };
            newFilter.sort = {
                field: dataKey,
                dir: sortType === 'asc' ? 'ASC' : 'DESC',
            };
            const newJson = stringifyFilter(newFilter);
            setOrder(sortType ?? 'asc');
            setOrderBy(dataKey);
            setJson(newJson);
            setFilter(newFilter);
            setFilterDirty(true);
            setJson4Preview(newJson);
        },
        [filter],
    );

    const errorPopupSettings: PopupSettings = useMemo(
        () => ({
            title: t(errorPopupTitle),
            noOk: true,
            textCancel: t('close'),
            needTranslation: false,
        }),
        [t, errorPopupTitle],
    );

    const showErrorPopup = errorPopupMessage > '';

    const handleBack = useCallback(() => {
        navigate(`/editor/element/report/${elementId}`);
    }, [navigate, elementId]);

    const handleRuleSelect = useCallback(
        (value: string) => {
            if (value !== String(reportAlertRuleId)) {
                navigate(`/editor/element/report/${elementId}/alert-rule/${value}`);
            }
        },
        [navigate, elementId, reportAlertRuleId],
    );

    const handleRuleAdded = useCallback(
        (newRuleId: number) => {
            navigate(`/editor/element/report/${elementId}/alert-rule/${newRuleId}`);
        },
        [navigate, elementId],
    );

    const handleRulesListReload = useCallback(() => {
        loadRuleData();
    }, []);

    const suggestion = useMemo(() => {
        if (!ruleData) return '';
        return buildDisplayTemplateSuggestion(ruleData.alertRuleData.name, ruleData.columnsData, filter);
    }, [ruleData, filter]);

    useEffect(() => {
        if (isDefaultTemplate) {
            displayTemplate.setRuleDisplayTemplate(suggestion);
        }
    }, [suggestion]); // isDefaultTemplate intentionally omitted: effect should only run when suggestion changes

    const onLeftTabChange = useCallback((index: number) => {
        setFilter((prevState) => {
            const newState = { ...prevState };
            newState.leftTab = index === 0 ? 'fields' : 'trackChanges';
            return newState;
        });
    }, []);

    const onInstanceModeUpdate = useCallback((newInstanceMode: InstanceMode) => {
        setInstanceMode(newInstanceMode);
        setFilter((prevState) => {
            const newState = { ...prevState };
            newState.history = newInstanceMode === 'lastTwo';

            if (newInstanceMode === 'lastTwo') {
                newState.leftTab = 'trackChanges';
            } else {
                newState.leftTab = 'fields';
            }

            return newState;
        });
        setFilterDirty(true);
        setBuilderDirty(true);
    }, []);

    useEffect(() => {
        if (ruleData && ruleData.datasetInstances.length > 0) {
            const instances = ruleData.datasetInstances;
            setCurrentInstance(instances[0].dataset_instance_id);

            if (instances.length > 1) {
                setPriorInstance(instances[1].dataset_instance_id);
            } else {
                setPriorInstance('');
            }
        }
    }, [ruleData]);

    const qBuilderLayout = useMemo(() => {
        return displayMode == 'page' ? 'all' : wizardStep == 0 ? 'fieldsOnly' : 'conditionOnly';
    }, [displayMode, wizardStep]);

    return ruleData ? (
        <Stack direction={'column'} alignItems="stretch" sx={{ height: 1, overflow: 'hidden' }}>
            {displayMode == 'page' && (
                <Infobar
                    rulesSelectData={rulesSelectData}
                    selectedRuleId={String(reportAlertRuleId)}
                    selectedRuleName={ruleData.alertRuleData.name}
                    onRuleSelect={handleRuleSelect}
                    onBack={handleBack}
                    onSave={saveRule}
                    filterDirty={filterDirty}
                    isPermitted4Edit={ruleData.isPermitted4Edit === 'Y'}
                    elementId={elementId}
                    onRuleAdded={handleRuleAdded}
                    onRulesListReload={handleRulesListReload}
                />
            )}

            {displayMode == 'wizard' && (
                <Stack className={displayMode == 'wizard' ? '' : 'd-none'}>
                    {wizardStep == 0 && (
                        <Box>
                            {nameComponent}
                            <ReportAlertInstanceModeSelection
                                instanceMode={instanceMode}
                                onInstanceModeUpdate={onInstanceModeUpdate}
                                showInstancesSelector={showInstancesSelector}
                            />
                        </Box>
                    )}
                    <Stack className={wizardStep == 1 && usedReportFields.length > 0 ? '' : 'd-none'}>
                        <SimpleFieldsSelectorTable
                            data={usedReportFields.map((column) => ({
                                label: column.label,
                                id: column.ref_column_name,
                            }))}
                            selectedColumns={filter.resultFields}
                            onSelectedChange={(value, checked) => {
                                let newList = filter.resultFields.slice();
                                const keysSet = new Set(value);
                                newList = checked
                                    ? [...new Set([...newList, ...value])]
                                    : newList.filter((field) => !keysSet.has(field));
                                handleFieldsChange(newList);
                            }}
                            type={'regular'}
                        />
                        <Box>
                            <TrackChangesTab
                                joinOnly={true}
                                showHistory={filter.history}
                                filter={structuredClone(filter)}
                                onTrackDataUpdate={(value) => {
                                    onCurrentFilterUpdate({ ...filter, joinFields: value.joinFields });
                                }}
                                fieldIds={ruleData.columnsData.map((field) => {
                                    return {
                                        id: field.id,
                                        name: field.name,
                                        column_name: field.column_name,
                                        ref_column_name: field.ref_column_name,
                                    };
                                })}
                            />
                        </Box>
                    </Stack>
                </Stack>
            )}

            {showInstancesSelector && (
                <Box className={displayMode == 'wizard' ? 'd-none' : ''} sx={displayMode == 'wizard' ? { pb: 2 } : {}}>
                    <InstanceInfo
                        datasetData={ruleData.datasetData}
                        datasetInstances={ruleData.datasetInstances}
                        lastUpdate={ruleData.updateFormattedDate}
                        currentInstance={currentInstance}
                        setCurrentInstance={setCurrentInstance}
                        priorInstance={priorInstance}
                        setPriorInstance={setPriorInstance}
                        instanceMode={instanceMode}
                        setInstanceMode={onInstanceModeUpdate}
                    />
                </Box>
            )}
            <Box className={qBuilderLayout == 'fieldsOnly' ? 'd-none' : ''}>
                <QueryBuilder
                    layout={qBuilderLayout}
                    columnsData={ruleData.columnsData}
                    selectedFilterData={null}
                    filter={cloneDeep(filter)}
                    onCurrentFilterUpdate={onCurrentFilterUpdate}
                    datasetId={ruleData.reportData.dataset_id}
                    multiInstancesMode={showInstancesSelector}
                    builderDirty={builderDirty}
                    json={json}
                    applyChanges={applyChanges}
                    onLeftTabChange={onLeftTabChange}
                    onFieldsChange={handleFieldsChange}
                    dataPreviewResized={dataPreviewResized}
                    conditionLabel={t('define_alert_rules')}
                    addRuleLabel={t('criteria')}
                    emptyLabel={t('no_criteria_yet')}
                    headerContent={
                        <DisplayTemplateField
                            columnsData={ruleData.columnsData}
                            displayTemplate={displayTemplate}
                            setIsDefaultTemplate={setIsDefaultTemplate}
                            setFilterDirty={setFilterDirty}
                            suggestion={suggestion}
                            noPadding={displayMode == 'wizard'}
                        />
                    }
                />
            </Box>
            {displayMode == 'page' && (
                <DataPreview
                    show
                    defaultHeight={350}
                    onResize={() => setDataPreviewResized((prev) => prev + 1)}
                    gridConfigUrl={dataPreviewUrl}
                    pkId={reportAlertRuleId.toString()}
                    uid={'report-alert-rule-preview'}
                    showLimitSelector
                    dataPayload={json4Preview}
                    infiniteScroll
                    onSortColumn={onSortColumn}
                    order={order}
                    orderBy={orderBy}
                />
            )}
            {showLoader && <LoadingPlaceholder variant={'secondary'} relativeToParent />}
            {showErrorPopup && (
                <ErrorPopup
                    open={showErrorPopup}
                    message={errorPopupMessage}
                    settings={errorPopupSettings}
                    onHide={() => setErrorPopupMessage('')}
                />
            )}
        </Stack>
    ) : null;
}
